import { RawUser, useUser } from '../hooks/use-user';
import { pickObject } from '../helpers/general';
import { Async, Branded } from '../helpers/types';
import { Influencer } from '@playbook-technologies/common-enums';
import {
  GetInfluencerQuery,
  useGetInfluencerQuery,
} from '../../../generated/graphql';
import { useAsyncResult } from '../helpers/gql';
import { makeAsync } from '../helpers/async';
import { useInfluencerId } from '@client/new/contexts/InfluencerIdContext';
import { SalesType } from './types';

export type UserRole =
  | 'Administrator'
  | 'Moderator'
  | 'Influencer'
  | 'ContentEditor';

export type UserLinks = {
  channelPage: string;
  landingPage: string;
  checkoutPage: string;
  giftPage: string;
};

export type LiteUser = Pick<RawUser, 'influencerId'> &
  Pick<
    RawUser['influencer'],
    | 'firstName'
    | 'id'
    | 'lastName'
    | 'paymentMethod'
    | 'stripeUserId'
    | 'isPaymentEnabled'
    | 'customURL'
    | 'profileDeepLink'
    | 'title'
    | 'profileInstagram'
    | 'mediaCover'
    | 'mediaAvatar'
    | 'facebookPixelId'
    | 'facebookConversionApiAccessToken'
    | 'ga4Configuration'
    | 'tikTokConfiguration'
    | 'collectionType'
    | 'productType'
    | 'publisheCollectionsCount'
    | 'publishedWorkoutsCount'
    | 'mode'
    | 'customizations'
    | 'visibility'
    | 'offering'
    | 'businessModel'
  > & {
    isLive: boolean;
    email?: string;
    links?: UserLinks;
    salesModel: SalesType;
  };
type ErrMsg = Branded<string, 'error'>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GQLUser = GetInfluencerQuery['creator'] & {
  influencerId: number;
  isPaymentEnabled: boolean;
};

const nullableUserKeys: Array<keyof GQLUser> = [
  'firstName',
  'lastName',
  'title',
  'email',
];

// TODO: (g) real validation is needed
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const toLiteUser = (v: any): LiteUser | ErrMsg => {
  if (!v || typeof v !== 'object') return 'invalid object' as ErrMsg;
  const isLive = Boolean(v.mode === Influencer.Mode.Live.value);
  const res = {
    ...pickObject(v as Required<GQLUser>, [
      'influencerId',
      'email',
      'firstName',
      'lastName',
      'id',
      'isPaymentEnabled',
      'profileDeepLink',
      'customURL',
      'stripeUserId',
      'paymentMethod',
      'mediaAvatar',
      'mediaCover',
      'title',
      'profileInstagram',
      'facebookPixelId',
      'facebookConversionApiAccessToken',
      'ga4Configuration',
      'tikTokConfiguration',
      'collectionType',
      'productType',
      'publisheCollectionsCount',
      'publishedWorkoutsCount',
      'mode',
      'customizations',
      'visibility',
      'offering',
      'businessModel',
    ]),
    isLive,
  };
  const salesModel: SalesType =
    v.salesModel === 'one-time-purchase' ? 'otp' : 'subscription'; // both || subscription
  nullableUserKeys.forEach((k) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (res as any)[k] === null ? ((res as any)[k] = '') : null,
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return { ...res, salesModel } as any as LiteUser;
};

// TODO: test this hook
export const useLiteUser = (): Async<LiteUser> => {
  const userData = useUser();
  if (!userData.influencerQuery.called) return { type: 'not-fetched' };
  if (userData.isLoading) return { type: 'fetching' };
  if (userData.influencerQuery.error)
    return {
      type: 'fetch-error',
      value: userData.influencerQuery.error.message,
    };
  const value = toLiteUser({
    ...userData.user.influencer,
    influencerId: userData.user.id,
    email: userData.user.email,
  });
  if (typeof value === 'string') return { type: 'fetch-error', value };

  return makeAsync.fetched(value);
};

const useInfluencerQuery = (influencerId: number): Async<LiteUser> => {
  const query = useGetInfluencerQuery({
    variables: { id: influencerId },
    fetchPolicy: 'no-cache',
    skip: !influencerId,
  });
  const asyncUser = useAsyncResult<GetInfluencerQuery>(query);
  if (asyncUser.type !== 'fetched') return asyncUser;

  const influencer = asyncUser.value.creator;
  if (!influencer) return { type: 'fetch-error', value: 'no creator set' };
  const value = toLiteUser({
    ...influencer,
    influencerId,
    isPaymentEnabled: true,
  });
  if (typeof value === 'string') return { type: 'fetch-error', value };

  return {
    type: 'fetched',
    value,
  };
};

export const useInfluencer = (creatorId?: number | null) => {
  const data = useUser();
  const res = useInfluencerQuery(creatorId ?? (data.influencer?.id || 0));
  if (res.type === 'fetched')
    return {
      type: 'fetched',
      value: { ...res.value, uid: data.user.id + '' },
    } as Async<LiteUser & { uid: string }>;
  return res;
};

export const useCreator = () => {
  const creatorId = useInfluencerId();
  const creator = useInfluencer(creatorId);
  return creator;
};
